package dev.lingvo.shared.screenshots.doc

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/** One drawable on a page. Serialized with the class discriminator `"type"`; an unknown type is refused. */
@Serializable
sealed interface Layer {
    val base: LayerBase
}

/** Fills the whole page; its box is ignored. At most one per page, and only at index 0. */
@Serializable
@SerialName("background")
data class BackgroundLayer(
    override val base: LayerBase,
    val fill: Paint,
    val blobs: List<Blob> = emptyList(),
    val grain: Grain? = null,
) : Layer

@Serializable
@SerialName("device")
data class DeviceLayer(
    override val base: LayerBase,
    val frame: FrameKind,
    val finish: FrameFinish = FrameFinish.AUTO,
    val screen: ImageSource = ImageSource.Capture(),
    val homeIndicator: Boolean = false,
    val glare: Boolean = false,
    /** Fraction of the screen width; only [FrameKind.NONE] reads it. */
    val screenRadius: Double? = null,
) : Layer

@Serializable
@SerialName("image")
data class ImageLayer(
    override val base: LayerBase,
    val source: ImageSource,
    val fit: ImageFit = ImageFit.COVER,
    val crop: NRect? = null,
    val cornerRadius: Double = 0.0,
    val stroke: StrokeSpec? = null,
) : Layer

@Serializable
@SerialName("text")
data class TextLayer(
    override val base: LayerBase,
    val content: TextContent,
    val style: TextStyle,
    val fit: FitSpec,
) : Layer

@Serializable
@SerialName("shape")
data class ShapeLayer(
    override val base: LayerBase,
    val shape: ShapeKind,
    val cornerRadius: Double = 0.0,
    val fill: Paint? = null,
    val stroke: StrokeSpec? = null,
    /** CURVE only: 2..16 points in box-local coordinates. */
    val points: List<Point> = emptyList(),
) : Layer

@Serializable
@SerialName("group")
data class GroupLayer(
    override val base: LayerBase,
    /** Children's boxes are in the group's local coordinates. Nesting is limited to 4 groups. */
    val children: List<Layer>,
    val clip: Boolean = false,
    val cornerRadius: Double = 0.0,
) : Layer

/** Where a text layer's words come from. Captions are strings in the project, so translators own them. */
@Serializable
sealed interface TextContent {
    /** The caption of the slot at [slot] in this page's strip. */
    @Serializable
    @SerialName("slotCaption")
    data class SlotCaption(val slot: Int = 0) : TextContent

    /** Any key in `store/<platform>/captions`. */
    @Serializable
    @SerialName("key")
    data class Key(val key: String) : TextContent

    /** The same text in every language (brand names, "01"). Warns unless digits only. */
    @Serializable
    @SerialName("literal")
    data class Literal(val text: String) : TextContent
}

@Serializable
sealed interface ImageSource {
    /** The capture of slot [slot]; [planOffset] reaches a neighbour in the plan (wrapping). */
    @Serializable
    @SerialName("capture")
    data class Capture(val slot: Int = 0, val planOffset: Int = 0) : ImageSource

    /** An uploaded asset by id, never a URL: the server fetches nothing on a document's behalf. */
    @Serializable
    @SerialName("asset")
    data class Asset(val assetId: String) : ImageSource

    @Serializable
    @SerialName("logo")
    data object BrandLogo : ImageSource
}

/** Page fractions: (0, 0) is the top-left of the page, (1, 1) the bottom-right. */
@Serializable
data class Blob(val cx: Double, val cy: Double, val r: Double, val color: ColorRef)

/** Film grain; [amount] is 0..0.15. */
@Serializable
data class Grain(val seed: Int, val amount: Double)

/** Layer-space offsets and blur; the renderer converts them to device pixels before drawing. */
@Serializable
data class Shadow(val dx: Double, val dy: Double, val blur: Double, val color: ColorRef)

@Serializable
data class StrokeSpec(val width: Double, val paint: Paint, val dash: List<Double> = emptyList())

/** A rectangle in 0..1 fractions of an image. */
@Serializable
data class NRect(val x: Double, val y: Double, val w: Double, val h: Double)

@Serializable
data class Point(val x: Double, val y: Double)

@Serializable
data class TextStyle(
    val role: TypeRole = TypeRole.DISPLAY,
    /** Null resolves to the brand or template family for [role]. */
    val family: String? = null,
    val weight: Int = 700,
    val italic: Boolean = false,
    val color: Paint,
    val lineHeight: Double = 1.1,
    val letterSpacingEm: Double = 0.0,
    val align: TextAlign = TextAlign.START,
    val vAlign: VAlign = VAlign.TOP,
    val transform: TextTransform = TextTransform.NONE,
    /** Wrap in the locale's quotation marks (`quoteStyleFor`). */
    val quoted: Boolean = false,
)

@Serializable
data class FitSpec(
    val maxSize: Double,
    val floorSize: Double,
    val maxLines: Int = 3,
    val hyphenation: HyphenationPolicy = HyphenationPolicy.SOFT_HYPHENS_ONLY,
    val balance: Boolean = true,
)
